import React, { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import {
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Grid,
  Paper,
  Step,
  Stepper,
  StepLabel,
  Typography,
} from "@material-ui/core";
import A_TextField from "../../../../Atoms/Inputs/TextFields/A_TextField";
import A_DatePicker from "../../../../Atoms/Inputs/DateTimePickers/A_DatePicker";
import A_Select from "../../../../Atoms/Inputs/Selects/A_Select";
import A_Button from "../../../../Atoms/Inputs/Buttons/A_Button";
import * as toastActions from "../../../../../../store/actions/toastActions";
import { toastSeverity } from "../../../../../../Utils/config/toastConfig";
import {
  ALL_FIELDS,
  BOOLEAN_OPTIONS,
  BORROWER_STEPS,
  REGISTERED_TO_OPERATING_ADDRESS_MAP,
  buildInitialState,
  buildBorrowerOptionSetObject,
  buildBorrowerStateFromSource,
  clearBorrowerDraft,
  copyRegisteredAddressToOperating,
  ExecuteSqlStoreProc,
  getBorrowerDraftStorageKey,
  getRecordSource,
  hasDraftableBorrowerData,
  isBorrowerFieldRequired,
  isOperatingAddressSynced,
  loadBorrowerDraft,
  parseBorrowerApiErrors,
  sanitizeBorrowerDraft,
  saveBorrowerDraft,
  shouldRenderBorrowerField,
  toOptionLabel,
  validateBorrowerStep,
} from "./Borrower_Form_helper";
import { httpPost } from "../../../../../../Utils/apis/apis";
import LookupSearchSelect from "../Add_Loan/LookupSearchSelect";
import { message } from "../../../../../../Utils/config/messages";
import { getCRMContactInfo } from "../../../../../../Utils/Helpers/common";
import { useSelector } from "react-redux";
import useStyles from "./Add_Borrower.styles";

const AddBorrowerComponent = ({
  formConfig,
  isEdit,
  isEditForm,
  setToast,
  onSaveSuccess,
}) => {
  const classes = useStyles();
  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState(() => buildInitialState());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [enumoptions, setEnumOptions] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [
    isOperatingAddressSameAsRegistered,
    setIsOperatingAddressSameAsRegistered,
  ] = useState(false);
  const [availableDraft, setAvailableDraft] = useState(null);
  const [isDraftBootstrapComplete, setIsDraftBootstrapComplete] =
    useState(false);
  const [isDraftSaving, setIsDraftSaving] = useState(false);
  const effectiveEditMode = isEdit || isEditForm;
  let { crmContactUserEmail } = getCRMContactInfo();
  const LoggedInUserEmail = useSelector((state) =>
    state.getLoggedInUserReducer?.data?.[0]?.properties?.email
      ? state.getLoggedInUserReducer?.data?.[0]?.properties?.email
      : crmContactUserEmail
  );
  const canUseDrafts = !effectiveEditMode;
  const draftOwnerIdentifier =
    LoggedInUserEmail || crmContactUserEmail || "anonymous";
  const draftStorageKey = canUseDrafts
    ? getBorrowerDraftStorageKey(draftOwnerIdentifier)
    : null;
  const borrowerType = (formData.BorrowerType || "").toLowerCase();

  const isIndividual = ["individual", "sole trader"].includes(borrowerType);
  const isNonIndividual = !isIndividual && borrowerType !== "";

  const currentStep = BORROWER_STEPS[activeStep];
  const isLastStep = activeStep === BORROWER_STEPS.length - 1;

  useEffect(() => {
    getEnumOptionsForField();
    if (!effectiveEditMode) {
      setFormData(buildInitialState());
      setIsOperatingAddressSameAsRegistered(false);
      return;
    }
    const source = getRecordSource(formConfig);
    setFormStateFromSource(source);
  }, [effectiveEditMode, formConfig]);

  useEffect(() => {
    let isMounted = true;

    const fetchDraft = async () => {
      if (!canUseDrafts || !draftStorageKey) {
        if (isMounted) {
          setAvailableDraft(null);
          setIsDraftBootstrapComplete(true);
        }
        return;
      }

      const loadedDraft = await loadBorrowerDraft(draftStorageKey);
      const draft = sanitizeBorrowerDraft(loadedDraft);

      if (!isMounted) {
        return;
      }

      setAvailableDraft(
        draft &&
          hasDraftableBorrowerData(
            draft.formData,
            draft.activeStep,
            draft.isOperatingAddressSameAsRegistered
          )
          ? draft
          : null
      );
      setIsDraftBootstrapComplete(true);
    };

    fetchDraft();

    return () => {
      isMounted = false;
    };
  }, [canUseDrafts, draftStorageKey]);

  const setFormStateFromSource = (source) => {
    const newState = buildBorrowerStateFromSource(source);
    setFormData(newState);
    setIsOperatingAddressSameAsRegistered(isOperatingAddressSynced(newState));
  };

  const dismissAvailableDraft = useCallback(() => {
    if (availableDraft) {
      setAvailableDraft(null);
    }
  }, [availableDraft]);

  useEffect(() => {
    setFormData((prev) => {
      const updated = { ...prev };
      if (isNonIndividual) {
        updated.DateOfBirth = "";
      }
      if (isIndividual) {
        updated.CompanyNumber = "";
        updated.IncorporationDate = "";
        updated.SicCodes = "";
        updated.VatNumber = "";
      }
      return updated;
    });
  }, [borrowerType]);

  const getEnumOptionsForField = async () => {
    const opionsetCategories = "BorrowerType,Country,PreferredContactMethod";
    try {
      setIsLoading(true);
      const payload = [
        {
          id: "Borrower_Dropdowns",
          spName: "[DF].[usp_GetOptionSetsByCategories]",
          parameters: {
            Categories: opionsetCategories,
          },
        },
      ];
      const response = await ExecuteSqlStoreProc(payload);
      const data = response?.Borrower_Dropdowns || [];
      const categories = opionsetCategories.split(",").map((c) => c.trim());
      const optionSetObject = buildBorrowerOptionSetObject(categories, data);
      if (Object.keys(optionSetObject).length !== 0) {
        setEnumOptions(optionSetObject);
      } else {
        setEnumOptions([]);
      }
    } catch (error) {
      setEnumOptions([]);
    } finally {
      setIsLoading(false);
    }
  };

  const getEnumOptions = (field) => {
    if (field.type === "boolean") {
      return BOOLEAN_OPTIONS;
    }
    const optionsFromEnum =
      field.backendKey === "OperatingAddressCountry"
        ? enumoptions["Country"]
        : enumoptions[field.backendKey] || [];
    if (optionsFromEnum.length === 0) {
      return [];
    }
    return optionsFromEnum.map((data) => ({
      id: data?.id,
      label: toOptionLabel(data?.label),
      value: data?.value,
    }));
  };

  const clearFieldError = (fieldId) => {
    setErrors((prev) => ({
      ...prev,
      [fieldId]: "",
    }));
  };

  const handleTextChange = (event) => {
    const { id, value } = event.target;
    dismissAvailableDraft();
    setFormData((prevState) => {
      const nextState = {
        ...prevState,
        [id]: value,
      };
      if (
        isOperatingAddressSameAsRegistered &&
        REGISTERED_TO_OPERATING_ADDRESS_MAP[id]
      ) {
        nextState[REGISTERED_TO_OPERATING_ADDRESS_MAP[id]] = value;
      }
      return nextState;
    });
    clearFieldError(id);
  };

  const handleDateChange = (value, id) => {
    dismissAvailableDraft();
    setFormData((prevState) => ({
      ...prevState,
      [id]: value,
    }));
    clearFieldError(id);
  };

  const handleSelectChange = (selectedOption, id) => {
    dismissAvailableDraft();
    setFormData((prevState) => ({
      ...prevState,
      [id]: selectedOption?.value || "",
    }));
    clearFieldError(id);
  };

  const handleLookupChange = (selected, id) => {
    const selectedValue = selected?.value || "";
    dismissAvailableDraft();
    setFormData((prevState) => {
      const nextState = {
        ...prevState,
        [id]: selectedValue,
      };
      if (
        isOperatingAddressSameAsRegistered &&
        REGISTERED_TO_OPERATING_ADDRESS_MAP[id]
      ) {
        nextState[REGISTERED_TO_OPERATING_ADDRESS_MAP[id]] = selectedValue;
      }
      return nextState;
    });
    clearFieldError(id);
  };

  const handleOperatingAddressSyncChange = (event) => {
    const checked = event.target.checked;
    dismissAvailableDraft();
    setIsOperatingAddressSameAsRegistered(checked);

    if (!checked) {
      return;
    }

    setFormData((prevState) => copyRegisteredAddressToOperating(prevState));
    setErrors((prevErrors) => {
      const nextErrors = { ...prevErrors };
      Object.values(REGISTERED_TO_OPERATING_ADDRESS_MAP).forEach(
        (operatingKey) => {
          nextErrors[operatingKey] = "";
        }
      );
      return nextErrors;
    });
  };

  const getFielderrorMessage = (field) => {
    if (errors[field.backendKey]) {
      return errors[field.backendKey];
    }
    return "";
  };

  const validateCurrentStep = () => {
    const newErrors = validateBorrowerStep({
      currentStep,
      formData,
      isIndividual,
      isNonIndividual,
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const shouldRenderField = (field) =>
    shouldRenderBorrowerField(field, isIndividual, isNonIndividual);

  const isFieldRequired = (field) =>
    isBorrowerFieldRequired(field, isIndividual, isNonIndividual);

  const handleNext = () => {
    const isValid = validateCurrentStep();
    if (!isValid) return;
    setActiveStep((prevStep) =>
      Math.min(prevStep + 1, BORROWER_STEPS.length - 1)
    );
  };

  const handleBack = () => {
    dismissAvailableDraft();
    setActiveStep((prevStep) => Math.max(prevStep - 1, 0));
  };

  const handleRestoreDraft = useCallback(() => {
    if (!availableDraft) {
      return;
    }

    setFormData(availableDraft.formData);
    setIsOperatingAddressSameAsRegistered(
      availableDraft.isOperatingAddressSameAsRegistered
    );
    setActiveStep(
      Math.min(
        Math.max(availableDraft.activeStep || 0, 0),
        BORROWER_STEPS.length - 1
      )
    );
    setErrors({});
    setAvailableDraft(null);
    setToast({
      type: toastSeverity.SUCCESS,
      severity: toastSeverity.SUCCESS,
      message: message.BORROWER_DRAFT_RESTORED,
      status: true,
      duration: 3000,
    });
  }, [availableDraft, setToast]);

  const handleDiscardDraft = useCallback(async () => {
    if (draftStorageKey) {
      await clearBorrowerDraft(draftStorageKey);
    }

    setAvailableDraft(null);
    setFormData(buildInitialState());
    setIsOperatingAddressSameAsRegistered(false);
    setActiveStep(0);
    setErrors({});
    setToast({
      type: toastSeverity.SUCCESS,
      severity: toastSeverity.SUCCESS,
      message: message.BORROWER_DRAFT_DISCARDED,
      status: true,
      duration: 3000,
    });
  }, [draftStorageKey, setToast]);

  const saveDraft = useCallback(
    async ({
      showEmptyWarning = true,
      successMessage = "Borrower draft saved.",
    } = {}) => {
      if (
        !canUseDrafts ||
        !draftStorageKey ||
        !isDraftBootstrapComplete ||
        isDraftSaving
      ) {
        return { saved: false, skipped: true };
      }

      if (
        !hasDraftableBorrowerData(
          formData,
          activeStep,
          isOperatingAddressSameAsRegistered
        )
      ) {
        if (showEmptyWarning) {
          setToast({
            type: toastSeverity.ERROR,
            severity: toastSeverity.ERROR,
            message: message.BORROWER_DRAFT_EMPTY_WARNING,
            status: true,
            duration: 3000,
          });
        }
        return { saved: false, skipped: true, empty: true };
      }

      setIsDraftSaving(true);

      const saved = await saveBorrowerDraft(draftStorageKey, {
        version: 1,
        activeStep,
        formData,
        isOperatingAddressSameAsRegistered,
        updatedAt: new Date().toISOString(),
      });

      setIsDraftSaving(false);

      setToast({
        type: saved ? toastSeverity.SUCCESS : toastSeverity.ERROR,
        severity: saved ? toastSeverity.SUCCESS : toastSeverity.ERROR,
        message: saved ? successMessage : message.SOMETHING_WENT_WRONG,
        status: true,
        duration: saved ? 3000 : 5000,
      });

      if (saved) {
        setAvailableDraft(null);
      }

      return { saved, skipped: false };
    },
    [
      canUseDrafts,
      draftStorageKey,
      isDraftBootstrapComplete,
      isDraftSaving,
      formData,
      activeStep,
      isOperatingAddressSameAsRegistered,
      setToast,
    ]
  );

  const handleSaveDraft = useCallback(() => saveDraft(), [saveDraft]);

  const handleFinish = async () => {
    const isValid = validateCurrentStep();
    if (!isValid) return;

    const payload = ALL_FIELDS.reduce((result, field) => {
      result[field.backendKey] = formData[field.backendKey];
      return result;
    }, {});

    setIsSubmitting(true);

    const insertPayload = {
      id: effectiveEditMode
        ? formData.LoanId || formData.ApplicationId || null
        : null,
      label: "borrower",
      type: "vertex",
      properties: payload,
      edges: [],
      security: {
        read: { groupids: [], users: [] },
        update: { groupids: [], users: [] },
        full: { groupids: [], users: [] },
        privateRecord: true,
      },
      duplicateDetection: null,
      submitToStoredProc: "DF.usp_AddBorrower",
    };

    try {
      const response = await httpPost(`/HTBCRMData/insert`, insertPayload);
      if (response && response.id != null) {
        if (canUseDrafts && draftStorageKey) {
          await clearBorrowerDraft(draftStorageKey);
        }
        if (onSaveSuccess) {
          onSaveSuccess(response.id);
        }
        return;
      }

      if (
        response?.response?.data?.errors &&
        Object.keys(response.response.data.errors).length > 0
      ) {
        const apiErrors = parseBorrowerApiErrors(response.response.data);
        setErrors((prev) => ({
          ...prev,
          ...apiErrors,
        }));
        const errorMessages = Object.values(apiErrors).join(". ");
        setToast({
          type: toastSeverity.ERROR,
          severity: toastSeverity.ERROR,
          message: errorMessages,
          status: true,
          duration: 5000,
        });
        setIsSubmitting(false);
        return;
      }

      const errorMessage =
        response?.response?.data?.Message ||
        "Failed to save Borrower. Please try again.";
      setToast({
        type: toastSeverity.ERROR,
        severity: toastSeverity.ERROR,
        message: errorMessage,
        status: true,
        duration: 5000,
      });
    } catch (error) {
      const errorResponse = error?.response?.data;
      if (
        errorResponse?.errors &&
        Object.keys(errorResponse.errors).length > 0
      ) {
        const apiErrors = parseBorrowerApiErrors(errorResponse);
        setErrors((prev) => ({
          ...prev,
          ...apiErrors,
        }));
        const errorMessages = Object.values(apiErrors).join(". ");
        setToast({
          type: toastSeverity.ERROR,
          severity: toastSeverity.ERROR,
          message: errorMessages,
          status: true,
          duration: 5000,
        });
        setIsSubmitting(false);
        return;
      }
      setToast({
        type: toastSeverity.ERROR,
        severity: toastSeverity.ERROR,
        message: "Failed to save Borrower. Please try again.",
        status: true,
        duration: 5000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderLoading = () => (
    <div
      style={{
        height: "72vh",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        gap: "10px",
      }}
    >
      <CircularProgress />
      <Typography variant="body2">Loading form...</Typography>
    </div>
  );

  const renderField = (field) => {
    if (field.type === "enum" || field.type === "boolean") {
      const options = getEnumOptions(field);
      return (
        <A_Select
          key={`${field.backendKey}-${formData[field.backendKey]}`}
          id={field.backendKey}
          label={field.label}
          fullWidth
          variant="standard"
          required={isFieldRequired(field)}
          defaultValue={formData[field.backendKey]}
          options={options}
          onChange={handleSelectChange}
          error={Boolean(errors[field.backendKey])}
          // fieldHelperText={getFieldHelperText(field)}
          helperText={getFielderrorMessage(field)}
        />
      );
    }
    if (field.type === "lookup") {
      const options = getEnumOptions(field);
      return (
        <LookupSearchSelect
          key={`${field.backendKey}-${options.length}`}
          id={field.backendKey}
          label={field.label}
          fullWidth
          required={isFieldRequired(field)}
          defaultValue={formData[field.backendKey]}
          options={options}
          multiple={!!field.multiple}
          onChange={handleLookupChange}
          // fieldHelperText={getFieldHelperText(field)}
          error={Boolean(errors[field.backendKey])}
          helperText={getFielderrorMessage(field)}
        />
      );
    }
    if (field.type === "date") {
      return (
        <A_DatePicker
          key={`${field.backendKey}`}
          id={field.backendKey}
          label={field.label}
          fullWidth
          margin="dense"
          inputVariant="standard"
          defaultValue={formData[field.backendKey] || null}
          onChange={handleDateChange}
          required={isFieldRequired(field)}
          error={Boolean(errors[field.backendKey])}
          // fieldHelperText={getFieldHelperText(field)}
          helperText={getFielderrorMessage(field)}
        />
      );
    }
    return (
      <A_TextField
        key={`${field.backendKey}`}
        id={field.backendKey}
        name={field.backendKey}
        label={field.label}
        variant={field.multiline ? "outlined" : "standard"}
        fullWidth
        defaultValue={formData[field.backendKey]}
        inputType="value"
        multiline={field.multiline}
        rows={field.rows}
        onChange={handleTextChange}
        required={isFieldRequired(field)}
        error={Boolean(errors[field.backendKey])}
        // fieldHelperText={getFieldHelperText(field)}
        helperText={getFielderrorMessage(field)}
      />
    );
  };

  if (isLoading) {
    return renderLoading();
  }

  return (
    <div className={classes.root}>
      <Paper elevation={0} className={classes.formCard}>
        {
          <>
            <div className={classes.stepperWrap}>
              <Stepper activeStep={activeStep} alternativeLabel>
                {BORROWER_STEPS.map((step) => (
                  <Step key={step.title}>
                    <StepLabel
                      classes={{
                        label: classes.stepLabel,
                        active: classes.stepLabelActive,
                        completed: classes.stepLabelCompleted,
                      }}
                    >
                      {step.title}
                    </StepLabel>
                  </Step>
                ))}
              </Stepper>
            </div>
            <div className={classes.fieldsContainer}>
              {availableDraft ? (
                <div className={classes.draftBanner}>
                  <div className={classes.draftBannerContent}>
                    <Typography className={classes.draftBannerTitle}>
                      Unsaved draft found
                    </Typography>
                    <Typography className={classes.draftBannerText}>
                      {availableDraft.updatedAt
                        ? `A borrower draft saved on ${new Date(
                            availableDraft.updatedAt
                          ).toLocaleString()} is available for your account.`
                        : "A previous borrower draft is available for your account."}
                    </Typography>
                  </div>
                  <div className={classes.draftBannerActions}>
                    <A_Button
                      label="Start fresh"
                      premiumVariant="secondary"
                      onClick={handleDiscardDraft}
                      disabled={isSubmitting || isLoading}
                    />
                    <A_Button
                      label="Resume draft"
                      premiumVariant="primary"
                      onClick={handleRestoreDraft}
                      disabled={isSubmitting || isLoading}
                    />
                  </div>
                </div>
              ) : null}
              <Typography variant="h6" className={classes.stepTitle}>
                {currentStep.title}
              </Typography>
              {currentStep.sections ? (
                currentStep.sections.map((section) => (
                  <div key={section.title} style={{ marginBottom: 24 }}>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: 12,
                        flexWrap: "wrap",
                        gap: 8,
                      }}
                    >
                      <Typography
                        variant="subtitle1"
                        style={{
                          fontWeight: 700,
                          color: "var(--color-primary-main)",
                          fontSize: 12,
                          textTransform: "uppercase",
                          letterSpacing: "0.06em",
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                        }}
                      >
                        <span
                          style={{
                            display: "inline-block",
                            width: 3,
                            height: 14,
                            backgroundColor: "var(--color-primary-main)",
                            borderRadius: 2,
                          }}
                        />
                        {section.title}
                      </Typography>
                      {section.title === "Operating Address" ? (
                        <FormControlLabel
                          control={
                            <Checkbox
                              color="primary"
                              checked={isOperatingAddressSameAsRegistered}
                              onChange={handleOperatingAddressSyncChange}
                            />
                          }
                          label="Same as Registered Address"
                          style={{ marginRight: 0 }}
                        />
                      ) : null}
                    </div>
                    <Grid container spacing={2}>
                      {section.fields
                        .filter((field) => shouldRenderField(field))
                        .map((field) => (
                          <Grid
                            item
                            xs={12}
                            sm={field.multiline ? 12 : 6}
                            key={field.backendKey}
                          >
                            {renderField(field)}
                          </Grid>
                        ))}
                    </Grid>
                  </div>
                ))
              ) : (
                <Grid container spacing={2}>
                  {currentStep.fields
                    .filter((field) => shouldRenderField(field))
                    .map((field) => (
                      <Grid
                        item
                        xs={12}
                        sm={field.multiline ? 12 : 6}
                        key={field.backendKey}
                      >
                        {renderField(field)}
                      </Grid>
                    ))}
                </Grid>
              )}
            </div>
            <div className={classes.actions}>
              <A_Button
                label="Back"
                premiumVariant="secondary"
                onClick={handleBack}
                disabled={activeStep === 0 || isSubmitting}
                className={classes.actionButton}
              />
              <div className={classes.actionGroup}>
                {canUseDrafts ? (
                  <A_Button
                    label={
                      isDraftSaving ? (
                        <CircularProgress size={18} color="inherit" />
                      ) : (
                        "Save Draft"
                      )
                    }
                    premiumVariant="secondary"
                    onClick={handleSaveDraft}
                    disabled={
                      isSubmitting || isDraftSaving || !isDraftBootstrapComplete
                    }
                    className={classes.actionButton}
                  />
                ) : null}
                {!isLastStep && (
                  <A_Button
                    label="Next"
                    premiumVariant="primary"
                    onClick={handleNext}
                    disabled={isSubmitting}
                    className={classes.actionButton}
                  />
                )}
                {isLastStep && (
                  <A_Button
                    label="Finish"
                    premiumVariant="primary"
                    onClick={handleFinish}
                    disabled={isSubmitting}
                    className={classes.actionButton}
                    startIcon={
                      isSubmitting ? (
                        <CircularProgress size={18} color="inherit" />
                      ) : null
                    }
                  />
                )}
              </div>
            </div>
          </>
        }
      </Paper>
    </div>
  );
};

export const Add_Borrower = AddBorrowerComponent;

AddBorrowerComponent.propTypes = {
  formConfig: PropTypes.any,
  isEdit: PropTypes.bool,
  isEditForm: PropTypes.bool,
  setToast: PropTypes.func,
  onClose: PropTypes.func,
  onSaveSuccess: PropTypes.func,
};

AddBorrowerComponent.defaultProps = {
  formConfig: null,
  isEdit: false,
  isEditForm: false,
  setToast: () => {},
  onClose: null,
  onSaveSuccess: null,
};

Add_Borrower.propTypes = AddBorrowerComponent.propTypes;
Add_Borrower.defaultProps = AddBorrowerComponent.defaultProps;

const mapStateToProps = () => ({});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(Object.assign({}, toastActions), dispatch);

export default connect(mapStateToProps, mapDispatchToProps, null)(Add_Borrower);
